// PR-UX-08 molecule · per-persona speech buffer card on session_running.
// Per FE_SPEC state_stores.md PersonaSpeechBuffer + tokens.md persona→palette mapping.
//
// FE-M2 (M0.5): palette source-of-truth moved to @/ui/theme/persona_palette.
// PersonaId widened from 6 → 13 RoleIDs (canonical type lives in @/store/session);
// unknown id falls back to paper-pearl via getPersonaPalette() (R4 persona
// resilience invariant). i18n calls use defaultValue so unmapped persona keys
// render the personaId itself instead of the raw key fragment.
//
// Visual: vertical card with persona name (in role's accent color), role description,
// streamed speech body. Live indicator (azure pulse) when chunks still arriving.

import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import type { PersonaId } from '@/store/session';
import { getPersonaPalette } from '@/ui/theme/persona_palette';

export type { PersonaId };

interface PersonaCardProps {
  personaId: PersonaId | string; // accept unknown ids; resolved via safe-fallback
  speechBody: string;            // accumulated chunks
  isFinal: boolean;              // is_final received
  monotonicIdLast?: number;
  /**
   * 1-based spoken-rank within current phase. 0 (or undefined) = persona has
   * not yet spoken this phase. Live.tsx computes this from monotonic_id_first
   * across the choreography speaker pool. Surfaces the speaker order so user
   * sees who spoke 1st / 2nd / 3rd. UX bug "发言看不到顺序" surfaced this.
   */
  spokenRank?: number;
  children?: ReactNode;          // optional: <IronKillStack> for iron
}

export function PersonaCard({
  personaId,
  speechBody,
  isFinal,
  monotonicIdLast,
  spokenRank,
  children,
}: PersonaCardProps) {
  const { t } = useTranslation();
  const palette = getPersonaPalette(personaId);
  const accentBorder = `border-l-${palette.accent}`;
  const accentText = `text-${palette.accent}-700`;
  const name = t(`persona.${personaId}.name`, { defaultValue: personaId });
  const role = t(`persona.${personaId}.role`, { defaultValue: '' });
  const isQueued = (spokenRank ?? 0) > 0;
  const hasSpoken = isQueued && (monotonicIdLast ?? 0) > 0;

  return (
    <article
      className={`bg-paper border-l-4 border-hairline ${accentBorder} px-4 py-3 rounded-r-md`}
      data-testid={`persona-card-${personaId}`}
      data-monotonic-id={monotonicIdLast}
      data-spoken-rank={spokenRank ?? 0}
      data-has-spoken={hasSpoken ? '1' : '0'}
      aria-busy={!isFinal}
    >
      <header className="flex items-baseline justify-between mb-2">
        <h3 className={`font-serif text-lg ${accentText} flex items-baseline gap-2`}>
          {isQueued && (
            <span
              className={
                'inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 rounded-full text-[10px] font-mono leading-none align-middle ' +
                (hasSpoken
                  ? 'bg-ink text-paper'
                  : 'border border-ink/40 bg-paper text-ink/60')
              }
              aria-label={t(hasSpoken ? 'persona.rank_aria' : 'persona.rank_queued_aria', {
                defaultValue: hasSpoken ? '第 {{n}} 位发言' : '第 {{n}} 位待发言',
                n: spokenRank,
              })}
              data-testid={`persona-rank-${personaId}`}
            >
              {spokenRank}
            </span>
          )}
          <span>{name}</span>
        </h3>
        <span className="text-xs font-mono tracking-hud text-ink-3">{role}</span>
      </header>
      <div className="text-sm text-ink leading-relaxed prose-persona">
        {speechBody ? (
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            // Disable raw HTML; safe-by-default. Tailwind classes shape spacing.
            components={{
              h1: ({ children }) => <h4 className="font-serif text-base font-medium mt-3 mb-1">{children}</h4>,
              h2: ({ children }) => <h4 className="font-serif text-base font-medium mt-3 mb-1">{children}</h4>,
              h3: ({ children }) => <h5 className="font-serif text-sm font-medium mt-2 mb-1">{children}</h5>,
              p:  ({ children }) => <p className="my-2 whitespace-pre-wrap">{children}</p>,
              ul: ({ children }) => <ul className="list-disc list-outside pl-5 my-2 space-y-0.5">{children}</ul>,
              ol: ({ children }) => <ol className="list-decimal list-outside pl-5 my-2 space-y-0.5">{children}</ol>,
              li: ({ children }) => <li className="leading-relaxed">{children}</li>,
              strong: ({ children }) => <strong className="font-semibold text-ink">{children}</strong>,
              em: ({ children }) => <em className="italic">{children}</em>,
              code: ({ children }) => <code className="font-mono text-xs bg-paper-2 px-1 py-0.5 rounded">{children}</code>,
              pre: ({ children }) => <pre className="font-mono text-xs bg-paper-2 p-2 rounded my-2 overflow-x-auto">{children}</pre>,
              blockquote: ({ children }) => <blockquote className="border-l-2 border-hairline pl-3 my-2 text-ink-2 italic">{children}</blockquote>,
              hr: () => <hr className="border-hairline my-3" />,
              a: ({ href, children }) => <a href={href} className="text-vermilion underline hover:no-underline" target="_blank" rel="noopener noreferrer">{children}</a>,
            }}
          >
            {speechBody}
          </ReactMarkdown>
        ) : (
          <em className="text-ink-3">{t('persona.waiting')}</em>
        )}
        {!isFinal && speechBody && <LiveCursor />}
      </div>
      {children}
    </article>
  );
}

function LiveCursor() {
  return (
    <span className="inline-block w-2 h-4 ml-1 bg-azure animate-pulse-soft align-middle" aria-hidden="true" />
  );
}

// PersonaGrid arranges 5 PersonaCards in council seating order:
//   sage (top, full width)
//   hammer / compass / horizon (3-col)
//   iron (full width, bottom)
export function PersonaGrid({ children }: { children: ReactNode }) {
  return <div className="grid gap-3 my-4">{children}</div>;
}
